import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBug, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaSync } from 'react-icons/fa';
import { testAPIConnection } from '../../api/fff';

/**
 * Composant temporaire de diagnostic API FFF
 * À utiliser uniquement en développement pour débugger les problèmes d'API
 */
const APIDebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  // N'afficher que en développement
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const runDiagnostic = async () => {
    setIsLoading(true);
    try {
      const testResults = await testAPIConnection();
      setResults(testResults);
    } catch (error) {
      setResults({
        apiAvailable: false,
        errors: ['Erreur lors du diagnostic: ' + error.message]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === true) return <FaCheckCircle className="text-green-500" />;
    if (status === false) return <FaTimesCircle className="text-red-500" />;
    return <FaExclamationTriangle className="text-yellow-500" />;
  };

  return (
    <>
      {/* Bouton flottant */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-[9999] bg-gradient-to-r from-red-500 to-orange-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <FaBug size={24} />
      </motion.button>

      {/* Panel de diagnostic */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 400 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 400 }}
            className="fixed top-0 right-0 h-screen w-[400px] bg-[#001B56] text-white shadow-2xl z-[9998] overflow-y-auto"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <FaBug className="text-[#FFE400]" size={24} />
                  <h2 className="text-xl font-bold">Diagnostic API FFF</h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white transition"
                >
                  ✕
                </button>
              </div>

              {/* Bouton de test */}
              <button
                onClick={runDiagnostic}
                disabled={isLoading}
                className="w-full bg-[#FFE400] text-[#001B56] py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#BDFF00] transition disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <FaSync className="animate-spin" />
                    Test en cours...
                  </>
                ) : (
                  <>
                    <FaSync />
                    Lancer le diagnostic
                  </>
                )}
              </button>

              {/* Résultats */}
              {results && (
                <div className="mt-6 space-y-4">
                  {/* Status général */}
                  <div className="bg-white/10 p-4 rounded-lg">
                    <h3 className="font-bold mb-3 text-[#FFE400]">Statut des Tests</h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span>API Accessible</span>
                        {getStatusIcon(results.apiAvailable)}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span>Données Club</span>
                        {getStatusIcon(results.clubDataOK)}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span>Données Équipes</span>
                        {getStatusIcon(results.equipesOK)}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span>Classement</span>
                        {getStatusIcon(results.classementOK)}
                      </div>
                    </div>
                  </div>

                  {/* Erreurs */}
                  {results.errors && results.errors.length > 0 && (
                    <div className="bg-red-500/20 border border-red-500 p-4 rounded-lg">
                      <h3 className="font-bold mb-2 flex items-center gap-2">
                        <FaTimesCircle />
                        Erreurs ({results.errors.length})
                      </h3>
                      <ul className="space-y-1 text-sm">
                        {results.errors.map((error, idx) => (
                          <li key={idx} className="text-red-200">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {results.warnings && results.warnings.length > 0 && (
                    <div className="bg-yellow-500/20 border border-yellow-500 p-4 rounded-lg">
                      <h3 className="font-bold mb-2 flex items-center gap-2">
                        <FaExclamationTriangle />
                        Avertissements ({results.warnings.length})
                      </h3>
                      <ul className="space-y-1 text-sm">
                        {results.warnings.map((warning, idx) => (
                          <li key={idx} className="text-yellow-200">• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommandations */}
                  <div className="bg-white/10 p-4 rounded-lg">
                    <h3 className="font-bold mb-2 text-[#FFE400]">Solutions</h3>
                    <ul className="space-y-2 text-sm">
                      {!results.apiAvailable && (
                        <li className="flex items-start gap-2">
                          <span className="text-[#FFE400] mt-1">→</span>
                          <span>L'API FFF est inaccessible. Vérifiez votre connexion Internet ou réessayez plus tard.</span>
                        </li>
                      )}
                      
                      {results.apiAvailable && !results.equipesOK && (
                        <li className="flex items-start gap-2">
                          <span className="text-[#FFE400] mt-1">→</span>
                          <span>La structure des données API a changé. Les protections du code devraient gérer cela automatiquement.</span>
                        </li>
                      )}
                      
                      {results.equipesOK && results.warnings.some(w => w.includes('@id')) && (
                        <li className="flex items-start gap-2">
                          <span className="text-[#FFE400] mt-1">→</span>
                          <span>Structure de données modifiée détectée. Le code utilise maintenant des fallbacks sécurisés.</span>
                        </li>
                      )}
                      
                      {results.apiAvailable && results.equipesOK && results.classementOK && (
                        <li className="flex items-start gap-2">
                          <span className="text-green-400 mt-1">✓</span>
                          <span className="text-green-300">Tout fonctionne correctement ! Si les données ne s'affichent pas, actualisez la page.</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Informations de debug */}
              <div className="mt-6 bg-white/5 p-4 rounded-lg text-xs">
                <h3 className="font-bold mb-2 text-gray-400">Informations</h3>
                <div className="space-y-1 text-gray-500">
                  <div>API: https://api-dofa.fff.fr/api</div>
                  <div>Club ID: 186901</div>
                  <div>Env: {process.env.NODE_ENV}</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default APIDebugPanel;

